import {
	AccessRouteForm,
	AdditionalInfoForm,
	BasicInfoForm,
	CareerInformationForm,
	CorporationForm,
	EducationForm,
	FCRegistrationForm,
	FamilyRelationshipForm,
	GStarClubForm,
	InsuranceExperienceForm,
	PersonalInfoForm,
	ProfessionalQualificationForm,
	TaxAccountantInfoForm
} from "@/features/tax-accountant";
import ETCInfoForm from "@/features/tax-accountant/ui/ETCInfoForm";
import { ITaxAccountantResModel, TaxAccountantResModel, useGetTaxAccountant, useModifyTaxAccountant } from "@/shared/api";
import { TaxAccountantModifyV2RequestModel } from "@/shared/api/tax-accountant/dto/TaxAccountantModifyV2RequestModel";
import { vars } from "@/shared/lib";
import { Button, Column, Row, Toast } from "@/shared/ui";
import { DeepKeys, standardSchemaValidator, useForm } from "@tanstack/react-form";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/(auth)/_layout/tax-accountant/$taxAccountantId/modify")({
	component: TaxAccountantModify
});

const VIEW_TYPE = "EDIT";

function TaxAccountantModify() {
	const router = useRouter();
	const { taxAccountantId } = useParams({ from: "/(auth)/_layout/tax-accountant/$taxAccountantId/modify" });

	const { data } = useGetTaxAccountant({ taxAccountantId });
	const { mutateAsync, reset: resetModify } = useModifyTaxAccountant();

	const [savedValues, setSavedValues] = useState<ITaxAccountantResModel>({
		...data,
		corporationInformation: data.corporationInformation
			? {
				...data.corporationInformation,
				familyInformations:
					data.corporationInformation.familyInformations.length > 0
						? data.corporationInformation.familyInformations
						: [
							{
								id: null,
								name: null,
								position: null,
								relationship: null,
								birthday: null,
								etc: null
							}
						]
			}
			: null,
		gStarClubs: data.gStarClubs ?? [],
		familyInformation: data.familyInformation
			? {
				...data.familyInformation,
				familyMemberInformations:
					data.familyInformation.familyMemberInformations.length > 0 &&
						data.familyInformation.familyMemberInformations.filter(
							(familyMemberInformation) => familyMemberInformation.relationship === "SPOUSE"
						).length > 0
						? [...data.familyInformation.familyMemberInformations]
						: [
							{
								id: null,
								birthYear: "",
								etc: "",
								gender: "UNKNOWN",
								job: "",
								name: "",
								relationship: "SPOUSE"
							},
							...data.familyInformation.familyMemberInformations
						]
			}
			: {
				maritalStatus: "UNKNOWN",
				familyMemberInformations: [
					{
						id: null,
						birthYear: "",
						etc: "",
						gender: "UNKNOWN",
						job: "",
						name: "",
						relationship: "SPOUSE"
					}
				]
			}
	});

	const form = useForm({
		defaultValues: savedValues,
		asyncAlways: true,
		asyncDebounceMs: 700,
		validatorAdapter: standardSchemaValidator(),
		validators: {
			onChangeAsync: TaxAccountantResModel.model,
			onChangeAsyncDebounceMs: 700,
			onBlurAsyncDebounceMs: 700
		},
		onSubmit: async ({ value, formApi }) => {
			const filteredMaritalStatus = value.familyInformation?.maritalStatus ?? "UNKNOWN";
			const filteredFamilyInfo =
				value.familyInformation?.familyMemberInformations.filter((familyMemberInformation) =>
					familyMemberInformation.relationship === "SPOUSE"
						? familyMemberInformation.job && familyMemberInformation.name
						: true
				) ?? [];

			try {
				const modifyValue = await TaxAccountantModifyV2RequestModel.schema.safeParseAsync({
					...value,
					familyInformation: { maritalStatus: filteredMaritalStatus, familyMemberInformations: filteredFamilyInfo },
					gStarClubs: value.gStarClubs ?? [],
					corporationInformation: {
						...value.corporationInformation,
						familyInformations: value.corporationInformation?.familyWorked === true ? value?.corporationInformation?.familyInformations.filter(info => info.name) : []
					},
					etcInformation: {
						carBrand: value.etcInformation?.carBrand,
						familyInsurancePlannerInformations: value.etcInformation?.familyInsurancePlannerInformations,
						assetSize: value.etcInformation?.assetSize,
						otherBusiness: value.etcInformation?.otherBusiness,
						contract: value.etcInformation?.contract,
						sns1: value.etcInformation?.sns1,
						sns2: value.etcInformation?.sns2,
						sns3: value.etcInformation?.sns3,
					}
				});

				if (modifyValue.success) {
					await mutateAsync(
						{ ...modifyValue.data, taxAccountantId },
						{
							onSuccess: () => {
								Toast.info("세무사 수정 신청되었습니다");
								router.history.replace(`/tax-accountant/${taxAccountantId}`);
							},
							onError: (error) => {
								if (Array.isArray(error.data)) {
									for (const data of error.data) {
										const fieldName = data.field as DeepKeys<ITaxAccountantResModel>;
										formApi.setFieldMeta(fieldName, (prevData) => {
											return {
												...prevData,
												errorMap: { onChange: data.reason }
											};
										});

										Toast.error(`${data.reason}`);
									}
								}

								formApi.reset();
								resetModify();
								setSavedValues(value);
							}
						}
					);
				} else {
					console.error("modify parse error", modifyValue.error);
				}
			} catch (error) {
				formApi.reset();
				console.error("세무사 수정 요청 중 오류 발생", error);
			}
		}
	});

	return (
		<Column fullWidth>
			<Row
				fullWidth
				style={{
					justifyContent: "flex-start",
					alignItems: "flex-start",
					gap: vars.size[48]
				}}
			>
				<Column
					fullWidth
					style={{
						justifyContent: "flex-start",
						alignItems: "flex-start",
						gap: vars.size[32]
					}}
				>
					<BasicInfoForm form={form} viewType={VIEW_TYPE} />
					<ProfessionalQualificationForm form={form} viewType={VIEW_TYPE} />
					<CorporationForm form={form} viewType={VIEW_TYPE} />
					<InsuranceExperienceForm form={form} viewType={VIEW_TYPE} />
					<AccessRouteForm form={form} viewType={VIEW_TYPE} />
					<FamilyRelationshipForm form={form} viewType={VIEW_TYPE} />
					<GStarClubForm viewType={VIEW_TYPE} form={form} />
				</Column>

				<Column
					fullWidth
					style={{
						justifyContent: "flex-start",
						alignItems: "flex-start",
						gap: vars.size[32]
					}}
				>
					<PersonalInfoForm form={form} viewType={VIEW_TYPE} />
					<AdditionalInfoForm form={form} viewType={VIEW_TYPE} />
					<ETCInfoForm form={form} viewType={VIEW_TYPE} />
				</Column>

				<Column
					fullWidth
					style={{
						justifyContent: "flex-start",
						alignItems: "flex-start",
						gap: vars.size[32]
					}}
				>
					<CareerInformationForm form={form} viewType={VIEW_TYPE} />
					<EducationForm form={form} viewType={VIEW_TYPE} />
					<TaxAccountantInfoForm form={form} viewType={VIEW_TYPE} />
					<FCRegistrationForm form={form} viewType={VIEW_TYPE} />
				</Column>
			</Row>

			<Row
				fullWidth
				style={{ justifyContent: "center", paddingBlock: vars.size[32], paddingInline: vars.size[16], gap: vars.size[4] }}
			>
				<Button color={"third"} size="large" onClick={() => router.history.back()}>
					{"뒤로가기"}
				</Button>
				<Button
					size="large"
					type="submit"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					{"수정요청"}
					{/* {"수정"} 권한에 따른 처리 */}
				</Button>
			</Row>
		</Column>
	);
}
